import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, useLocation } from 'react-router-dom';
import loadable from '@loadable/component';
import { Footer, LoadingBar, MainContainer, PathChangeListener } from '@agco-onedigital/ecosystem-lib-web-components';
import { commonMessages } from 'messages';
import { EAuthRoutes, FSEAuthRoutes } from '@agco-onedigital/ecosystem-lib-frontend-utils';

import { handleLogoutRedirect } from '../../utils/msalConfiguration/utils';
import { useLaunchDarkly } from '../../store/LaunchDarkly';
import { useUser } from '../../store/User';
import ScrollToTop from '../../components/ScrollToTop';
import PageNotFound from '../../components/PageNotFound';
import FSEModuleLoader from '../../components/ModuleLoader/FSEModuleLoader';
import InactivityModal from '../../components/InactivityModal';
import FocusReset from '../../components/FocusReset';
import SiteUnderMaintenance from '../../pages/SiteUnderMaintenance';
import Sidebar from '../Sidebar';

import AuthRouter from './AuthRouter';
import Authenticated from './Authenticated';
import LoginError from './LoginError';
import LoginSyncWait from './LoginSyncWait';
import { EPreAuthRoutes } from './types';
import { AppContainer, ContentWrapper } from './styles';

const PopStateBroadcaster = () => {
  const location = useLocation();

  useEffect(() => {
    window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
  }, [location]);

  return null;
};

const LoginLoading = loadable(() => import('../../pages/LoginLoading'), {
  fallback: <LoadingBar />,
});

export const messages = commonMessages.authTemplates.footer;

const Registration = loadable(() => import('../../pages/Registration'), {
  fallback: <LoadingBar />,
});
const Dashboard = loadable(() => import('../../pages/Dashboard'), {
  fallback: <LoadingBar />,
});
const LogInAndSecurity = loadable(() => import('../../pages/User/LogInAndSecurity'), {
  fallback: <LoadingBar />,
});
const PersonalData = loadable(() => import('../../pages/User/PersonalData'), {
  fallback: <LoadingBar />,
});
const DataAndPrivacy = loadable(() => import('../../pages/User/DataAndPrivacy'), {
  fallback: <LoadingBar />,
});
const Fleet = loadable(() => import('../../pages/Fleet'));
const FleetDetails = loadable(() => import('../../pages/FleetDetails'));
const AddMachine = loadable(() => import('../../pages/AddMachine'), {
  fallback: <LoadingBar />,
});
const VerifyMachine = loadable(() => import('../../pages/VerifyMachine'), {
  fallback: <LoadingBar />,
});
const ClaimBusiness = loadable(() => import('../../pages/ClaimBusiness'), {
  fallback: <LoadingBar />,
});
const SmartFarming = loadable(() => import('../../pages/SmartFarming'), {
  fallback: <LoadingBar />,
});
const SettingsBusinessAccounts = loadable(() => import('../../pages/Settings/BusinessAccounts'), {
  fallback: <LoadingBar />,
});

const authenticatedRoutes = [
  {
    path: EAuthRoutes.DASHBOARD,
    component: Dashboard,
  },
  {
    path: `${EAuthRoutes.DASHBOARD}${EAuthRoutes.CLAIM_BUSINESS}`,
    component: ClaimBusiness,
  },
  {
    path: `${EAuthRoutes.USER}${EAuthRoutes.LOGIN_AND_SECURITY}`,
    component: LogInAndSecurity,
  },
  {
    path: `${EAuthRoutes.USER}${EAuthRoutes.PERSONAL_DATA}`,
    component: PersonalData,
  },
  {
    path: `${EAuthRoutes.SETTINGS}${EAuthRoutes.PROFILE}`,
    component: PersonalData,
  },
  {
    path: `${EAuthRoutes.USER}${EAuthRoutes.MARKETING_PREFERENCES}`,
    component: DataAndPrivacy,
  },
  {
    path: EAuthRoutes.SETTINGS_BUSINESS_ACCOUNTS,
    component: SettingsBusinessAccounts,
  },
  {
    path: EAuthRoutes.USER_BUSINESS_ACCOUNTS,
    component: SettingsBusinessAccounts,
  },
  {
    path: `${EAuthRoutes.LEGACY_FLEET_LIST}`,
    component: Fleet,
  },
  {
    path: `${EAuthRoutes.LEGACY_FLEET_LIST}/:id/*`,
    component: FleetDetails,
  },
  {
    path: `${EAuthRoutes.FLEET_OVERVIEW}`,
    component: Fleet,
  },
  {
    path: `${EAuthRoutes.FLEET_OVERVIEW}/warranty`,
    component: Fleet,
  },
  {
    path: `${EAuthRoutes.FLEET_OVERVIEW}/psps`,
    component: Fleet,
  },
  {
    path: `${EAuthRoutes.FLEET_OVERVIEW}/:id/*`,
    component: FleetDetails,
  },
  {
    path: EAuthRoutes.SERVICE_AND_INFORMATION,
    component: () => <PageNotFound errorCode="404" />,
  },
  {
    path: `${EAuthRoutes.SERVICE_AND_INFORMATION}${EAuthRoutes.ADD_MACHINE}`,
    component: AddMachine,
  },
  {
    path: `${EAuthRoutes.SERVICE_AND_INFORMATION}${EAuthRoutes.VERIFY_MACHINE}`,
    component: VerifyMachine,
  },
  {
    path: `${EAuthRoutes.SMART_FARMING}`,
    component: SmartFarming,
  },
];

export const EmptyComponent = () => null;

const fseRoutes = [
  ...Object.values(FSEAuthRoutes).map(path => ({
    path: `${path}/*`,
    component: EmptyComponent,
  })),
];

const fallbackRoute = {
  path: '*',
  component: () => <PageNotFound errorCode="404" />,
};

export const router = createBrowserRouter([
  {
    path: EPreAuthRoutes.REGISTRATION,
    element: <Registration />,
  },
  {
    path: EPreAuthRoutes.HOME,
    element: <AuthRouter />,
  },
  {
    path: EPreAuthRoutes.LOGIN_ERROR,
    element: <LoginError />,
  },
  {
    path: EPreAuthRoutes.LOGIN_LOADING,
    element: <LoginLoading />,
  },
  {
    path: EPreAuthRoutes.LOGIN_SYNC_WAIT,
    element: <LoginSyncWait />,
  },
  ...[...authenticatedRoutes, ...fseRoutes, fallbackRoute].map(({ path, component: Component }) => ({
    path,
    element: (
      <Authenticated>
        <Sidebar />
        <ScrollToTop />
        <FocusReset />
        <PathChangeListener />
        <InactivityModal />
        <PopStateBroadcaster />
        <MainContainer>
          <ContentWrapper>
            <Component />
            <FSEModuleLoader />
          </ContentWrapper>
          <Footer messages={messages} />
        </MainContainer>
      </Authenticated>
    ),
  })),
  {
    path: '*',
    element: <PageNotFound errorCode="404" />,
  },
]);

const AppNavigation = () => {
  const launchDarkly = useLaunchDarkly();
  const user = useUser();
  useEffect(() => {
    if (user.isFailedToLoad && !user.isLoading) {
      void handleLogoutRedirect(launchDarkly, false, true);
    }
  }, [user.isFailedToLoad, user.isLoading, launchDarkly]);

  if (launchDarkly?.['site-under-maintenance']) {
    return <SiteUnderMaintenance />;
  }

  return (
    <AppContainer>
      <RouterProvider router={router} />
    </AppContainer>
  );
};

export default AppNavigation;
